import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { find, get, map } from 'lodash';
import queryString from 'query-string';
import { history } from '../../../store/history';
import { DropDown } from '../Filters/DropDown';

import { changeLanguage } from '../../../store/actions/settings';
import { getLocale } from '../../../store/selectors/settings';

import { LOCALES } from '../../../utils/locale';

interface LanguageOption {
    label: string;
    value: string;
}

const selectOptions: LanguageOption[] = map(LOCALES, (value, key) => ({
    label: key,
    value
}));

const QUERY_PARAM = 'lang';

export const GlobalLanguageSelector = () => {
    const dispatch = useDispatch();
    const locale = useSelector(getLocale);
    const defaultLocale = useMemo(() => find(selectOptions, selectOption => selectOption.value === locale), [locale]);
    const [selectedValue, setSelectedValue] = useState<LanguageOption | undefined>(defaultLocale);
    const queryParams = useMemo(() => queryString.parse(history.location.search), [history.location.search]);
    const langQueryParam = get(queryParams, QUERY_PARAM, defaultLocale?.label);

    const onChange = useCallback(
        (nextLocale: string | number | null) => {
            if (!nextLocale || typeof nextLocale !== 'string') {
                return;
            }
            const option = find(selectOptions, selectOption => selectOption.value === nextLocale);
            if (!option) return;

            const nextQueryParams = { ...queryString.parse(history.location.search) };
            dispatch(changeLanguage(option.value));
            setSelectedValue(option);
            nextQueryParams.lang = option.label;
            history.replace({
                ...history.location,
                search: queryString.stringify(nextQueryParams)
            });
        },
        [dispatch]
    );

    useEffect(() => {
        setSelectedValue(defaultLocale);
    }, [defaultLocale]);

    useEffect(() => {
        if (!langQueryParam) {
            return;
        }

        const initialValue = find(selectOptions, selectOption => selectOption.label === langQueryParam);

        if (!initialValue) {
            return;
        }

        if (initialValue.value !== locale) {
            dispatch(changeLanguage(initialValue.value));
        }

        if (selectedValue?.value !== initialValue.value) {
            setSelectedValue(initialValue);
        }
    }, [dispatch, langQueryParam, locale, selectedValue?.value]);

    useEffect(() => {
        let initialValue = defaultLocale;
        if (langQueryParam) {
            initialValue = find(selectOptions, selectOption => selectOption.label === langQueryParam);
        }
        if (selectedValue?.value !== initialValue?.value) {
            setSelectedValue(initialValue);
        }
    }, [defaultLocale, langQueryParam, selectedValue?.value]);

    return (
        <DropDown
            dataTest="global-language-selector"
            values={selectOptions}
            selectedValue={selectedValue?.value ?? defaultLocale?.value ?? ''}
            onValueChanged={onChange}
            width={85}
            marginRight={20}
            size="auto"
            compact
        />
    );
};
